import { PageProps } from 'gatsby'
import React, { useEffect } from 'react'

import { BlogButton } from '@application/Interfaces/Features/BlogButton'
import { BlogContent } from '@application/Interfaces/Features/BlogContent'
import { BlogList } from '@application/Interfaces/Features/BlogList'
import { BlogsThumbnailCards } from '@application/Interfaces/Features/BlogsThumbnailCards'
import { Breadcrumb } from '@application/Interfaces/Features/Breadcrumb'
import { CardsWithTab } from '@application/Interfaces/Features/CardsWithTab'
import { CBMCalculator } from '@application/Interfaces/Features/CBMCalculator'
import { CookieMessage } from '@application/Interfaces/Features/CookieMessage'
import { Footer } from '@application/Interfaces/Features/Footer/Footer'
import { FreightResourcesList } from '@application/Interfaces/Features/FreightResourcesList'
import { GlobalFreightGuideList } from '@application/Interfaces/Features/GlobalFreightGuideList'
import { Header } from '@application/Interfaces/Features/Header/Header'
import { HeroLandingView } from '@application/Interfaces/Features/HeroLandingView'
import { HeroProfile } from '@application/Interfaces/Features/HeroProfile'
import { HeroSection } from '@application/Interfaces/Features/HeroSection/HeroSection'
import { HorizontalLinks } from '@application/Interfaces/Features/HorizontalLinks'
import { IconTitle } from '@application/Interfaces/Features/IconTitle'
import { IconTitleDescription } from '@application/Interfaces/Features/IconTitleDescription/IconTitleDescription'
import { IconTitleDescriptionImage } from '@application/Interfaces/Features/IconTitleDescriptionImage'
import { IconTitleLink } from '@application/Interfaces/Features/IconTitleLink'
import { ImageDescriptionSlideout } from '@application/Interfaces/Features/ImageDescriptionSlideout'
import { ImageHoverDescription } from '@application/Interfaces/Features/ImageHoverDescription'
import { ImageHoverDescriptionLarge } from '@application/Interfaces/Features/ImageHoverDescriptionLarge/ImageHoverDescriptionLarge'
import { ImageNumberTitleDescription } from '@application/Interfaces/Features/ImageNumberTitleDescription'
import { ImageSlider } from '@application/Interfaces/Features/ImageSlider/ImageSlider'
import { ImageTitleDetailsCards } from '@application/Interfaces/Features/ImageTitleDetailsCards'
import { LogosComponent } from '@application/Interfaces/Features/LogosComponent'
import { NoteAlert } from '@application/Interfaces/Features/NoteAlert'
import { PardotForm } from '@application/Interfaces/Features/PardotForm'
import { SingleTitleDescriptionCTA } from '@application/Interfaces/Features/SingleTitleDescriptionCTA'
import { SitemapList } from '@application/Interfaces/Features/SitemapList'
import { SocialFeed } from '@application/Interfaces/Features/SocialFeed'
import { StaticTradelane } from '@application/Interfaces/Features/StaticTradelane'
import { Stats } from '@application/Interfaces/Features/Stats'
import { SubTitleDescriptionCTAImage } from '@application/Interfaces/Features/SubTitleDescriptionCTAImage'
import { Table } from '@application/Interfaces/Features/Table'
import { Testimonial } from '@application/Interfaces/Features/Testimonial'
import { TitleDescription } from '@application/Interfaces/Features/TitleDescription'
import { TitleDescriptionAccordion } from '@application/Interfaces/Features/TitleDescriptionAccordion'
import { TitleDescriptionBullets } from '@application/Interfaces/Features/TitleDescriptionBullets'
import { TitleDescriptionCards } from '@application/Interfaces/Features/TitleDescriptionCards'
import { TitleDescriptionMap } from '@application/Interfaces/Features/TitleDescriptionMap'
import { Tradelanes } from '@application/Interfaces/Features/Tradelanes'
import { USP } from '@application/Interfaces/Features/USP'
import { VerticalLinks } from '@application/Interfaces/Features/VerticalLinks'
import { Video } from '@application/Interfaces/Features/Video'
import { useGetThemeDirection } from '@application/Interfaces/Hooks/useGetThemeDirection/useGetThemeDirection'
import { useSetGrowthBookAttributes } from '@application/Interfaces/Hooks/useGrowthBook'
import type {
    // AgilitySectionType,
    BlogButtonType,
    BlogContentType,
    BlogDetailsType,
    BlogListType,
    BlogType,
    BreadcrumbPropsType,
    CardsWithTabComponentType,
    CBMComponentType,
    FreightListType,
    GraphBarType,
    GraphLineType,
    GraphPieType,
    HelpCenterType,
    HeroLandingViewType,
    HeroProfileComponentType,
    HeroSectionComponent,
    HorizontalLinksType,
    IconTitleDescriptionImageType,
    IconTitleDescriptionType,
    IconTitleLinkType,
    IconTitleType,
    ImageDescriptionSlideoutType,
    ImageHoverDescriptionLargeType,
    ImageHoverDescriptionType,
    ImageNumberTitleDescriptionType,
    ImageSliderType,
    ImageTitleDetailsCardsType,
    LogosType,
    NoteAlertType,
    OurOfficesType,
    PageNodeType,
    PardotFormType,
    SingleTitleDescriptionCTAType,
    SingleTypeComponentType,
    SitemapListType,
    SocialFeedType,
    StaticTradelaneType,
    StatsType,
    SubTitleDescriptionCTAImageType,
    TableType,
    TestimonialComponent,
    TitleComponentType,
    TitleDescriptionAccordionType,
    TitleDescriptionBulletsType,
    TitleDescriptionCardsType,
    TitleDescriptionMapType,
    TitleDescriptionType,
    TradelanesType,
    USPType,
    VerticalLinksType,
    VideoType,
} from '@application/Types/ComponentTypes'
import type { Locale } from '@application/Types/Locale'
import { Nullable } from '@application/Types/Opt'
import { STRAPI_COMPONENT_TYPES } from '@core/Constants/PageConstants'
import { isCookieAccepted } from '@src/Infrastructure/Cookies'
import { sendCookiePermissionDefaultGa4Event } from '@src/Infrastructure/GoogleTagManager/GA4Events'

import { BlogDetails } from '../BlogDetails/BlogDetails'
import { Graph } from '../Graph/Graph'
import { HelpCenterList } from '../HelpCenterList'
import { OurOffices } from '../OurOffices'
import { Title } from '../Title'

export const PageBuilder = (
    props: PageProps<{ strapiPage: PageNodeType }, { language: Locale; singleTypeComponents?: SingleTypeComponentType }>
): Nullable<JSX.Element> => {
    const {
        pageContext: { language, singleTypeComponents },
        location,
        data: { strapiPage: pageData },
    } = props

    useSetGrowthBookAttributes()

    const direction = useGetThemeDirection()

    useEffect(() => {
        const analyticsStorage = isCookieAccepted('performance') ? 'granted' : 'denied'
        const adStorage = isCookieAccepted('marketing') ? 'granted' : 'denied'
        sendCookiePermissionDefaultGa4Event(analyticsStorage, adStorage)
    }, [])

    if (!pageData) return null

    const {
        pageComponents,
        alternateUrls,
        headerTheme,
        headerType = 'Default',
        footerTheme,
        footerType = 'Default',
    } = pageData

    const singleTypeLogoCarousel = singleTypeComponents?.['carousel'][language]

    return (
        <div dir={direction}>
            <CookieMessage />
            <Header language={language} mode={headerTheme} type={headerType} alternateUrls={alternateUrls} />
            <div data-testid="components">
                {pageComponents?.map((componentData) => {
                    switch (componentData.strapi_component) {
                        case STRAPI_COMPONENT_TYPES.HERO_SECTION:
                            return <HeroSection {...(componentData as HeroSectionComponent)} key={componentData.id} />

                        case STRAPI_COMPONENT_TYPES.USP_SECTION:
                            return <USP {...(componentData as USPType)} key={componentData.id} />

                        case STRAPI_COMPONENT_TYPES.BREADCRUMB:
                            return <Breadcrumb {...(componentData as BreadcrumbPropsType)} key={componentData.id} />

                        // TODO uncomment once needed again
                        // case STRAPI_COMPONENT_TYPES.AGILITY_SECTION:
                        //     return <AgilitySection {...(componentData as AgilitySectionType)} key={componentData.id} />

                        case STRAPI_COMPONENT_TYPES.SERVICES_SECTION:
                            return (
                                <ImageHoverDescription
                                    {...(componentData as ImageHoverDescriptionType)}
                                    key={componentData.id}
                                />
                            )

                        case STRAPI_COMPONENT_TYPES.ADDITIONAL_SERVICES:
                            return (
                                <IconTitleDescription
                                    {...(componentData as IconTitleDescriptionType)}
                                    key={componentData.id}
                                />
                            )

                        case STRAPI_COMPONENT_TYPES.APP_SCREENSHOTS:
                            return (
                                <SubTitleDescriptionCTAImage
                                    {...(componentData as SubTitleDescriptionCTAImageType)}
                                    key={componentData.id}
                                />
                            )

                        case STRAPI_COMPONENT_TYPES.CONTACT_US:
                            return (
                                <ImageHoverDescriptionLarge
                                    {...(componentData as ImageHoverDescriptionLargeType)}
                                    key={componentData.id}
                                />
                            )

                        case STRAPI_COMPONENT_TYPES.COMPANY_INFO:
                            return <Stats {...(componentData as StatsType)} key={componentData.id} />

                        case STRAPI_COMPONENT_TYPES.BLOG:
                            return <BlogsThumbnailCards {...(componentData as BlogType)} key={componentData.id} />

                        case STRAPI_COMPONENT_TYPES.TESTIMONIAL:
                            return <Testimonial {...(componentData as TestimonialComponent)} key={componentData.id} />

                        case STRAPI_COMPONENT_TYPES.TOOLS:
                            return <IconTitleLink {...(componentData as IconTitleLinkType)} key={componentData.id} />

                        case STRAPI_COMPONENT_TYPES.ICON_TITLE:
                            return <IconTitle {...(componentData as IconTitleType)} key={componentData.id} />

                        case STRAPI_COMPONENT_TYPES.LOGOS_SECTION:
                            return <LogosComponent {...(componentData as LogosType)} key={componentData.id} />

                        case STRAPI_COMPONENT_TYPES.ROUTES_SECTION:
                            return (
                                <StaticTradelane {...(componentData as StaticTradelaneType)} key={componentData.id} />
                            )

                        case STRAPI_COMPONENT_TYPES.TRADELANES:
                            return <Tradelanes {...(componentData as TradelanesType)} key={componentData.id} />

                        case STRAPI_COMPONENT_TYPES.SOCIAL_SECTION:
                            return <SocialFeed {...(componentData as SocialFeedType)} key={componentData.id} />

                        case STRAPI_COMPONENT_TYPES.CONTACT_FORM:
                            return <PardotForm {...(componentData as PardotFormType)} key={componentData.id} />

                        case STRAPI_COMPONENT_TYPES.OFFICES_SECTION:
                            return <OurOffices {...(componentData as OurOfficesType)} key={componentData.id} />

                        case STRAPI_COMPONENT_TYPES.OUR_STORY:
                            return (
                                <ImageNumberTitleDescription
                                    {...(componentData as ImageNumberTitleDescriptionType)}
                                    key={componentData.id}
                                />
                            )

                        case STRAPI_COMPONENT_TYPES.HERO_LANDING_VIEW:
                            return (
                                <HeroLandingView {...(componentData as HeroLandingViewType)} key={componentData.id} />
                            )

                        case STRAPI_COMPONENT_TYPES.OUR_TEAM:
                            return (
                                <ImageDescriptionSlideout
                                    {...(componentData as ImageDescriptionSlideoutType)}
                                    key={componentData.id}
                                />
                            )

                        case STRAPI_COMPONENT_TYPES.OUR_VALUE_PROPOSITION:
                            return (
                                <IconTitleDescriptionImage
                                    {...(componentData as IconTitleDescriptionImageType)}
                                    key={componentData.id}
                                />
                            )
                        case STRAPI_COMPONENT_TYPES.IMAGE_SLIDER:
                            return <ImageSlider {...(componentData as ImageSliderType)} key={componentData.id} />

                        case STRAPI_COMPONENT_TYPES.TITLE_DESCRIPTION_BULLETS:
                            return (
                                <TitleDescriptionBullets
                                    {...(componentData as TitleDescriptionBulletsType)}
                                    key={componentData.id}
                                />
                            )

                        case STRAPI_COMPONENT_TYPES.TITLE_DESCRIPTION:
                            return (
                                <TitleDescription {...(componentData as TitleDescriptionType)} key={componentData.id} />
                            )

                        case STRAPI_COMPONENT_TYPES.TITLE_DESCRIPTION_ACCORDION:
                            return (
                                <TitleDescriptionAccordion
                                    {...(componentData as TitleDescriptionAccordionType)}
                                    key={componentData.id}
                                />
                            )

                        case STRAPI_COMPONENT_TYPES.GRAPH:
                            return (
                                <Graph {...(componentData as GraphLineType)} graphType="line" key={componentData.id} />
                            )

                        case STRAPI_COMPONENT_TYPES.GRAPH_BAR:
                            return <Graph {...(componentData as GraphBarType)} graphType="bar" key={componentData.id} />

                        case STRAPI_COMPONENT_TYPES.GRAPH_PIE:
                            return <Graph {...(componentData as GraphPieType)} graphType="pie" key={componentData.id} />

                        case STRAPI_COMPONENT_TYPES.CBM_CALCULATOR:
                            return <CBMCalculator {...(componentData as CBMComponentType)} key={componentData.id} />

                        case STRAPI_COMPONENT_TYPES.HORIZONTAL_LINKS:
                            return (
                                <HorizontalLinks {...(componentData as HorizontalLinksType)} key={componentData.id} />
                            )

                        case STRAPI_COMPONENT_TYPES.VERTICAL_LINKS:
                            return <VerticalLinks {...(componentData as VerticalLinksType)} key={componentData.id} />

                        case STRAPI_COMPONENT_TYPES.TABLE:
                            return <Table {...(componentData as TableType)} key={componentData.id} />

                        case STRAPI_COMPONENT_TYPES.BLOG_LIST:
                            return <BlogList {...(componentData as BlogListType)} key={componentData.id} />

                        case STRAPI_COMPONENT_TYPES.HELP_CENTER_LIST:
                            return <HelpCenterList {...(componentData as HelpCenterType)} key={componentData.id} />

                        case STRAPI_COMPONENT_TYPES.SINGLE_TITLE_DESCRIPTION_CTA:
                            return (
                                <SingleTitleDescriptionCTA
                                    {...(componentData as SingleTitleDescriptionCTAType)}
                                    key={componentData.id}
                                />
                            )

                        case STRAPI_COMPONENT_TYPES.BLOG_DETAILS:
                            return <BlogDetails {...(componentData as BlogDetailsType)} key={componentData.id} />

                        case STRAPI_COMPONENT_TYPES.BLOG_CONTENT:
                            return (
                                <BlogContent
                                    components={[componentData as BlogContentType]}
                                    id={componentData.id}
                                    strapi_component={componentData.strapi_component}
                                    key={componentData.id}
                                    withWrapper
                                />
                            )

                        case STRAPI_COMPONENT_TYPES.BLOG_BUTTON:
                            return <BlogButton {...(componentData as BlogButtonType)} key={componentData.id} />

                        case STRAPI_COMPONENT_TYPES.TITLE_DESCRIPTION_CARDS:
                            return (
                                <TitleDescriptionCards
                                    {...(componentData as TitleDescriptionCardsType)}
                                    key={componentData.id}
                                />
                            )

                        case STRAPI_COMPONENT_TYPES.IMAGE_TITLE_DETAIL_CARDS:
                            return (
                                <ImageTitleDetailsCards
                                    {...(componentData as ImageTitleDetailsCardsType)}
                                    key={componentData.id}
                                />
                            )

                        case STRAPI_COMPONENT_TYPES.TITLE_DESCRIPTION_MAP:
                            return (
                                <TitleDescriptionMap
                                    {...(componentData as TitleDescriptionMapType)}
                                    key={componentData.id}
                                />
                            )

                        case STRAPI_COMPONENT_TYPES.GLOBAL_FREIGHT_GUIDE_LIST:
                            return (
                                <GlobalFreightGuideList
                                    {...(componentData as FreightListType)}
                                    language={language}
                                    key={componentData.id}
                                />
                            )

                        case STRAPI_COMPONENT_TYPES.FREIGHT_RESOURCES_LIST:
                            return (
                                <FreightResourcesList
                                    {...(componentData as FreightListType)}
                                    language={language}
                                    key={componentData.id}
                                />
                            )

                        case STRAPI_COMPONENT_TYPES.VIDEO:
                            return <Video {...(componentData as VideoType)} key={componentData.id} />

                        case STRAPI_COMPONENT_TYPES.NOTE_ALERT:
                            return <NoteAlert {...(componentData as NoteAlertType)} key={componentData.id} />

                        case STRAPI_COMPONENT_TYPES.HERO_PROFILE:
                            return (
                                <HeroProfile
                                    {...(componentData as HeroProfileComponentType)}
                                    logoSlideItems={singleTypeLogoCarousel}
                                    key={componentData.id}
                                />
                            )

                        case STRAPI_COMPONENT_TYPES.TITLE:
                            return <Title {...(componentData as TitleComponentType)} key={componentData.id} />

                        case STRAPI_COMPONENT_TYPES.SITEMAP_LIST:
                            return <SitemapList {...(componentData as SitemapListType)} key={componentData.id} />

                        case STRAPI_COMPONENT_TYPES.CARD_WITH_TABS:
                            return (
                                <CardsWithTab
                                    {...(componentData as CardsWithTabComponentType)}
                                    key={componentData.id}
                                />
                            )

                        default:
                            return null
                    }
                })}
            </div>
            <Footer language={language} mode={footerTheme} type={footerType} logoSlideItems={singleTypeLogoCarousel} />
        </div>
    )
}
